import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { Eye, EyeOff } from 'lucide-react';
import { auth } from '../services/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';

function LoginPage() {
  const theme = useTheme();
  const { appName, logoUrl } = useTenant();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for demo mode and auto-login
  useEffect(() => {
    const isDemo = searchParams.get('demo') === 'true';
    if (isDemo) {
      setEmail('demo@demo.com');
      setPassword('example');
      // Auto-submit after a brief delay to show the form
      const timeoutId = setTimeout(async () => {
        setLoading(true);
        try {
          await login('demo@demo.com', 'example');
          navigate('/');  // Go to HomeRedirect which determines tenant-specific landing page
        } catch (error) {
          setError(getErrorMessage(error));
          setLoading(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [searchParams, login, navigate]);

  const getErrorMessage = (error) => {
    // Convert Firebase error codes to user-friendly messages
    const errorCode = error.code || error.message;

    if (errorCode.includes('auth/invalid-credential') ||
        errorCode.includes('auth/wrong-password') ||
        errorCode.includes('auth/user-not-found')) {
      return 'Incorrect email or password. Please try again.';
    }

    if (errorCode.includes('auth/invalid-email')) {
      return 'Please enter a valid email address.';
    }

    if (errorCode.includes('auth/user-disabled')) {
      return 'This account has been disabled. Please contact support.';
    }

    if (errorCode.includes('auth/too-many-requests')) {
      return 'Too many failed login attempts. Please try again later or reset your password.';
    }

    // Default message for unknown errors
    return 'Unable to log in. Please check your credentials and try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');  // Go to HomeRedirect which determines tenant-specific landing page
    } catch (error) {
      setError(getErrorMessage(error));
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
    } catch (error) {
      const errorCode = error.code || error.message;

      if (errorCode.includes('auth/user-not-found')) {
        setError('No account found with this email address.');
      } else if (errorCode.includes('auth/invalid-email')) {
        setError('Please enter a valid email address.');
      } else if (errorCode.includes('auth/too-many-requests')) {
        setError('Too many requests. Please try again later.');
      } else {
        setError('Unable to send password reset email. Please try again.');
      }
    }
    setLoading(false);
  };

  const styles = getStyles(theme);

  return (
    <div style={styles.container}>
      <div style={styles.loginBox}>
        {logoUrl && (
          <div style={styles.logoContainer}>
            <img src={logoUrl} alt={appName} style={styles.logo} />
          </div>
        )}
        <h2 style={styles.title}>{appName} Login</h2>
        {error && <div style={styles.error}>{error}</div>}
        {resetEmailSent && (
          <div style={styles.success}>
            Password reset email sent! Check your inbox.
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            required
          />
          <div style={styles.passwordContainer}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.passwordInput}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={styles.eyeButton}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          <button type="submit" disabled={loading} style={styles.button}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        <div style={styles.forgotPasswordContainer}>
          <button
            type="button"
            onClick={handleForgotPassword}
            style={styles.forgotPasswordLink}
            disabled={loading}
          >
            Forgot Password?
          </button>
        </div>
        <div style={styles.demoContainer}>
          <button
            type="button"
            onClick={() => navigate('/demo')}
            style={styles.demoLink}
          >
            Watch demo videos
          </button>
        </div>
      </div>
    </div>
  );
}

const getStyles = (theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: theme.palette.background.default
  },
  loginBox: {
    backgroundColor: theme.palette.background.paper,
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    width: '400px'
  },
  logoContainer: {
    textAlign: 'center',
    marginBottom: '1.5rem'
  },
  logo: {
    maxHeight: '60px',
    maxWidth: '200px',
    objectFit: 'contain'
  },
  title: {
    textAlign: 'center',
    marginBottom: '1.5rem',
    color: theme.palette.text.primary
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    marginBottom: '1rem',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box'
  },
  passwordContainer: {
    position: 'relative',
    width: '100%',
    marginBottom: '1rem'
  },
  passwordInput: {
    width: '100%',
    padding: '0.75rem',
    paddingRight: '3rem',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box'
  },
  eyeButton: {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    padding: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.3s ease'
  },
  button: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  error: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
    padding: '0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem'
  },
  success: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
    padding: '0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem'
  },
  forgotPasswordContainer: {
    marginTop: '1rem',
    textAlign: 'center'
  },
  forgotPasswordLink: {
    background: 'none',
    border: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: '0.9rem',
    padding: '0.5rem'
  },
  demoContainer: {
    marginTop: '0.25rem',
    textAlign: 'center'
  },
  demoLink: {
    background: 'none',
    border: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 600,
    padding: '0.5rem'
  }
});

export default LoginPage;
